import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { loadReorderWords } from './reorderWords';
import type { Announcements, DragEndEvent, ScreenReaderInstructions } from '@dnd-kit/core';
import type { useSortable } from '@dnd-kit/sortable';
import { afterBootRequests } from '@/services/bootRequests';
import type { SortableSensorsType } from './sortableKit';

/**
 * T-0770 (`docs/concept-mobile-app.md`, LC-6a): the reorder lists without dnd-kit in the entry.
 *
 * The sidebar's namespaces and work areas and a work area's pages are drawn on the first screen, and
 * dnd-kit (about 40 kB) was in the entry for them, though nobody drags before the screen is up. Here
 * a list draws its rows as they are, with no drag; `sortableKit.tsx` loads once the boot has sent its
 * own requests.
 *
 * ⛔ LC-6e (I-1841): no row is drawn again when the kit comes in. A list keeps a registry of its rows
 * (`SortableRegistry`), given to them through `SortableArea`'s context, which is drawn from the
 * first frame. Once the kit is in, the list's drag context is drawn BESIDE its rows, never around
 * them, with one binder per row (`sortableKit.tsx`, `LiveSortableBinders`): the binder calls
 * `useSortable`, takes the row's element from the registry, and hands the row its attributes,
 * listeners and transform back through it. The row's element is the same before and after, so a
 * keyboard user's focus, the list's scroll and a screen reader's cursor stay where they were.
 */
type SortableKitType = typeof import('./sortableKit');
type SortableStateType = ReturnType<typeof useSortable>;
type SortableArgsType = Parameters<typeof useSortable>[0];

let kit: SortableKitType | null = null;
let loading: Promise<SortableKitType> | null = null;
const listeners = new Set<() => void>();

/** Loads the kit once, after the boot's requests; a failed load is tried again by the next list. */
export function loadSortableKit(): Promise<SortableKitType> {
  loading ??= afterBootRequests()
    // I-2453: what a drag says comes with the kit, so a drag is never announced as raw keys.
    .then(() => Promise.all([import('./sortableKit'), loadReorderWords()]))
    .then(([loaded]) => loaded)
    .then((loaded) => {
      kit = loaded;
      for (const listener of listeners) listener();
      return loaded;
    })
    .catch((error: unknown) => {
      loading = null;
      throw error;
    });
  return loading;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The kit once it is in, else null; asking starts the load. */
export function useSortableKit(): SortableKitType | null {
  const current = useSyncExternalStore(
    subscribe,
    () => kit,
    () => null,
  );
  useEffect(() => {
    if (!current) void loadSortableKit().catch(() => undefined);
  }, [current]);
  return current;
}

/** What a row takes from its binder: what `useSortable` gives a row, the transform as CSS. */
export interface IRowBinding {
  attributes: SortableStateType['attributes'];
  listeners: SortableStateType['listeners'];
  transformCss: string | undefined;
  transition: string | undefined;
  isDragging: boolean;
}

interface IRegisteredRow {
  args: SortableArgsType;
  node: HTMLElement | null;
  binding: IRowBinding | null;
  /** Moves when the row's arguments or element change, so its binder takes them up. */
  version: number;
}

/** The handle events dnd-kit's sensors start a drag from, as a row spreads them. */
const GESTURE_STARTS = ['onTouchStart', 'onPointerDown', 'onMouseDown'] as const;
type GestureStartType = (typeof GESTURE_STARTS)[number];
const GESTURE_ENDS = ['touchend', 'touchcancel', 'pointerup', 'pointercancel', 'mouseup'] as const;

/**
 * I-1840 (LC-6e): a press on a row's handle made before the kit is in. Its start events are kept
 * until the press ends; if the row's binder comes in while the finger or the button is still down,
 * the binder's own listener is given the start event, so dnd-kit's sensor takes up the gesture where
 * it is and the drag lands. Without it, the first drag after a launch under load did nothing.
 */
interface IHeldGesture {
  registry: SortableRegistry;
  id: string;
  starts: Partial<Record<GestureStartType, Event>>;
}
let heldGesture: IHeldGesture | null = null;

function holdGesture(registry: SortableRegistry, id: string, type: GestureStartType, event: Event) {
  if (heldGesture && (heldGesture.registry !== registry || heldGesture.id !== id))
    heldGesture = null;
  if (!heldGesture) {
    const gesture: IHeldGesture = { registry, id, starts: {} };
    heldGesture = gesture;
    const end = () => {
      if (heldGesture === gesture) heldGesture = null;
      for (const name of GESTURE_ENDS) window.removeEventListener(name, end, true);
    };
    for (const name of GESTURE_ENDS) window.addEventListener(name, end, true);
  }
  heldGesture.starts[type] = event;
  // The kit is wanted now: a press is the first use of it.
  void loadSortableKit().catch(() => undefined);
}

/** Hands a held press on this row to the listeners the row's binder has just given it, once. */
function adoptHeldGesture(registry: SortableRegistry, id: string, binding: IRowBinding): void {
  const gesture = heldGesture;
  if (!gesture || gesture.registry !== registry || gesture.id !== id) return;
  const listeners = binding.listeners as Partial<
    Record<GestureStartType, (event: unknown) => void>
  >;
  for (const type of GESTURE_STARTS) {
    const event = gesture.starts[type];
    const listener = listeners?.[type];
    if (!event || !listener) continue;
    heldGesture = null;
    // dnd-kit's activators read the native event, and skip one a handler has already taken.
    listener({
      nativeEvent: event,
      target: event.target,
      defaultPrevented: event.defaultPrevented,
    });
    return;
  }
}

const sameBinding = (a: IRowBinding | null, b: IRowBinding | null): boolean =>
  a === b ||
  (a !== null &&
    b !== null &&
    a.attributes === b.attributes &&
    a.listeners === b.listeners &&
    a.transformCss === b.transformCss &&
    a.transition === b.transition &&
    a.isDragging === b.isDragging);

/**
 * One list's rows, between the rows (which register their arguments and element) and the binders
 * (which publish what `useSortable` gives each row). Before the kit is in nobody publishes, and a row
 * draws as it is.
 */
export class SortableRegistry {
  private readonly rows = new Map<string, IRegisteredRow>();
  /** A row's element, given by its ref before the row's layout effect registers it. */
  private readonly earlyNodes = new Map<string, HTMLElement>();
  private ids: readonly string[] = [];
  private readonly rowListeners = new Map<string, Set<() => void>>();
  private readonly listListeners = new Set<() => void>();

  /** The ids of the rows drawn now, in the order they registered. */
  rowIds(): readonly string[] {
    return this.ids;
  }

  row(id: string): IRegisteredRow | undefined {
    return this.rows.get(id);
  }

  binding(id: string): IRowBinding | null {
    return this.rows.get(id)?.binding ?? null;
  }

  register(id: string, args: SortableArgsType): void {
    const row = this.rows.get(id);
    if (!row) {
      const node = this.earlyNodes.get(id) ?? null;
      this.earlyNodes.delete(id);
      this.rows.set(id, { args, node, binding: null, version: 0 });
      this.ids = [...this.ids, id];
      this.notifyList();
      return;
    }
    if (row.args.disabled === args.disabled && row.args.id === args.id) return;
    row.args = args;
    row.version += 1;
    this.notifyList();
  }

  unregister(id: string): void {
    if (!this.rows.delete(id)) return;
    this.ids = this.ids.filter((rowId) => rowId !== id);
    this.notifyList();
  }

  setNode(id: string, node: HTMLElement | null): void {
    const row = this.rows.get(id);
    if (!row) {
      // React attaches a ref before it runs the row's layout effect, so the element can come first.
      if (node) this.earlyNodes.set(id, node);
      else this.earlyNodes.delete(id);
      return;
    }
    if (row.node === node) return;
    row.node = node;
    row.version += 1;
    this.notifyList();
  }

  publish(id: string, binding: IRowBinding | null): void {
    const row = this.rows.get(id);
    if (!row || sameBinding(row.binding, binding)) return;
    row.binding = binding;
    for (const listener of this.rowListeners.get(id) ?? []) listener();
    if (binding) adoptHeldGesture(this, id, binding);
  }

  subscribeRow(id: string, listener: () => void): () => void {
    const set = this.rowListeners.get(id) ?? new Set();
    set.add(listener);
    this.rowListeners.set(id, set);
    return () => set.delete(listener);
  }

  subscribeList = (listener: () => void): (() => void) => {
    this.listListeners.add(listener);
    return () => this.listListeners.delete(listener);
  };

  private notifyList(): void {
    for (const listener of this.listListeners) listener();
  }
}

const SortableRegistryContext = createContext<SortableRegistry | null>(null);

/** What a row spreads: on its element `setNodeRef` and the transform, on its handle the rest. */
export interface IRowSortable extends Omit<IRowBinding, 'attributes'> {
  attributes: Partial<SortableStateType['attributes']>;
  setNodeRef: (node: HTMLElement | null) => void;
}

/**
 * A row's sortable state, from its list's binder once the kit is in, and nothing before: the same
 * hooks on every render, so the row is never drawn again for the kit (LC-6e).
 */
export function useRowSortable(args: SortableArgsType): IRowSortable {
  const registry = useContext(SortableRegistryContext);
  const id = String(args.id);
  // The id as the kit types it, and `disabled`: all a list asks of a row.
  const { id: rowId, disabled } = args;
  useLayoutEffect(() => {
    registry?.register(id, { id: rowId, disabled });
  }, [registry, id, rowId, disabled]);
  useLayoutEffect(() => {
    if (!registry) return undefined;
    return () => registry.unregister(id);
  }, [registry, id]);
  const subscribeRow = useCallback(
    (listener: () => void) => registry?.subscribeRow(id, listener) ?? (() => undefined),
    [registry, id],
  );
  const binding = useSyncExternalStore(
    subscribeRow,
    () => registry?.binding(id) ?? null,
    () => null,
  );
  // The row's id on its element (tests and a screen reader's place read it), and the element for
  // its binder.
  const setNodeRef = useCallback(
    (node: HTMLElement | null) => {
      if (node) node.dataset.sortableRow = id;
      registry?.setNode(id, node);
    },
    [registry, id],
  );
  // Before the kit is in, a press on the handle is held for the binder to take up (I-1840).
  const holdingListeners = useMemo(() => {
    if (!registry) return undefined;
    const holder =
      (type: GestureStartType) =>
      (event: { nativeEvent: Event }): void =>
        holdGesture(registry, id, type, event.nativeEvent);
    return Object.fromEntries(
      GESTURE_STARTS.map((type) => [type, holder(type)]),
    ) as SortableStateType['listeners'];
  }, [registry, id]);
  return {
    attributes: binding?.attributes ?? {},
    listeners: binding ? binding.listeners : holdingListeners,
    transformCss: binding?.transformCss,
    transition: binding?.transition,
    isDragging: binding?.isDragging ?? false,
    setNodeRef,
  };
}

/**
 * A list's rows, with its drag context beside them once the kit is in. The rows are the first child
 * from the first frame on, so the kit's arrival adds a sibling and draws no row again (LC-6e).
 */
export function SortableArea({
  current,
  ids,
  sensors,
  onDragEnd,
  announcements,
  screenReaderInstructions,
  children,
}: {
  current: SortableKitType | null;
  ids: readonly string[];
  sensors: SortableSensorsType;
  onDragEnd: (event: DragEndEvent) => void;
  announcements?: Announcements;
  screenReaderInstructions?: ScreenReaderInstructions;
  children: ReactNode;
}) {
  const registry = useMemo(() => new SortableRegistry(), []);
  const Binders = current?.LiveSortableBinders;
  return (
    <SortableRegistryContext.Provider value={registry}>
      {children}
      {Binders ? (
        <Binders
          registry={registry}
          ids={ids}
          sensors={sensors}
          onDragEnd={onDragEnd}
          announcements={announcements}
          screenReaderInstructions={screenReaderInstructions}
        />
      ) : null}
    </SortableRegistryContext.Provider>
  );
}
